import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { track, AnalyticsEvents } from '@/lib/services/analytics'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useShareSettings } from './useShareSettings'
import { ShareModalShell } from './ShareModalShell'
import {
  DEFAULT_ACCESS_ICONS,
  GeneralAccessSelect,
  GuestChatAccessSection,
  OrgVisibilityToggle,
  PeopleWithAccessSection,
  ShareModalFooter,
  ShareAccessMode,
  ShareAccessOption,
} from './ShareAccessSections'
import { MemberSearchInput } from './MemberSearchInput'
import { CollaboratorList } from './CollaboratorList'
import type { ShareSettingsAdapter } from './ShareSettingsTypes'

export interface StudyMeta {
  name: string
  coverImageUrl?: string
  respondentCount?: number
  questionCount?: number
  date?: Date | string
  populationId?: string
  description?: string
}

interface ShareStudyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  adapter: ShareSettingsAdapter
  studyMeta: StudyMeta
  onOpen?: () => void
}

export function ShareStudyModal(props: ShareStudyModalProps) {
  const { open, onOpenChange, adapter, onOpen } = props
  const { t } = useTranslation('app')
  const { t: tCommon } = useTranslation('common')

  const share = useShareSettings({ adapter, open, onOpen })
  const { org } = useCurrentOrg()
  const [linkCopied, setLinkCopied] = useState(false)
  const [activeTab, setActiveTab] = useState<'access' | 'collaborate'>('access')

  const orgName = org?.name || t('sharing.organization')

  // Dropdown follows sharingMode. isPublic stays on for invite modes so
  // guest emails reach an OTP-gated /share/:token URL. "Anyone with the
  // link" is sharingMode=shared (no OTP).
  const [selectedDropdownMode, setSelectedDropdownMode] = useState<ShareAccessMode>('private')

  useEffect(() => {
    if (!share.loading && !share.loadError) {
      if (share.sharingMode === 'shared') {
        setSelectedDropdownMode('shared')
      } else if (share.sharingMode === 'organization') {
        setSelectedDropdownMode('organization')
      } else {
        setSelectedDropdownMode('private')
      }
    }
  }, [share.loading, share.loadError, share.sharingMode])

  const isOrgVisibleToggleOn = selectedDropdownMode === 'shared' && share.sharingMode === 'organization'

  const accessOptions: Array<ShareAccessOption> = useMemo(() => [
    { value: 'private', label: t('sharing.restricted'), description: t('sharing.onlyInvitedEmailVerified'), icon: DEFAULT_ACCESS_ICONS.private },
    { value: 'organization', label: orgName, description: t('sharing.everyoneInOrg'), icon: DEFAULT_ACCESS_ICONS.organization },
    { value: 'shared', label: t('sharing.anyoneWithLinkLabel'), description: t('sharing.anyoneNoEmailVerification'), icon: DEFAULT_ACCESS_ICONS.shared },
  ], [orgName, t])

  const orgMemberEmails = useMemo(
    () => new Set(share.orgMembers.map((m) => m.email.toLowerCase())),
    [share.orgMembers],
  )

  async function handleAccessChange(mode: ShareAccessMode) {
    setSelectedDropdownMode(mode)
    // Invite modes (restricted / org) keep the share URL live so guest emails
    // work. OTP vs open-link is sharingMode, not isPublic. disableSharing is
    // the only way to fully kill the URL.
    if (!share.isPublic) {
      await share.handleToggleLinkSharing(true)
    }
    await share.handleSelectMode(mode)
  }

  async function handleOrgVisibilityToggle(visible: boolean) {
    if (visible) {
      await share.handleSelectMode('organization')
    } else {
      await share.handleSelectMode('shared')
    }
  }

  async function handleCopyLink() {
    if (!share.shareUrl) return
    try {
      await navigator.clipboard.writeText(share.shareUrl)
      track(AnalyticsEvents.REFERRAL_LINK_COPIED, { source: 'study_share' })
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
      toast.success(t('sharing.linkCopied'))
    } catch { /* clipboard unavailable */ }
  }

  return (
    <ShareModalShell
      open={open}
      onOpenChange={onOpenChange}
      title={tCommon('share')}
      testId="share-study-modal"
      tabs={[
        { id: 'access', label: t('sharing.access') },
        { id: 'collaborate', label: t('sharing.collaborate') },
      ]}
      activeTab={activeTab}
      onTabChange={(tabId) => setActiveTab(tabId as 'access' | 'collaborate')}
      loading={share.loading}
      loadError={share.loadError}
      onRetry={share.loadData}
      footer={activeTab === 'access' ? (
        <ShareModalFooter
          shareUrl={share.shareUrl}
          saving={share.saving}
          copied={linkCopied}
          onCopy={handleCopyLink}
          onDone={() => onOpenChange(false)}
        />
      ) : null}
    >
      {activeTab === 'access' && (
        <div className="flex flex-col gap-0">
          <div className="flex flex-col gap-3 px-6 py-5">
            <span className="text-xs font-medium text-muted-foreground">
              {t('sharing.generalAccess')}
            </span>
            <GeneralAccessSelect
              value={selectedDropdownMode}
              options={accessOptions}
              onChange={handleAccessChange}
              disabled={share.saving}
            />

            {selectedDropdownMode === 'shared' && (
              <div className="flex flex-col gap-2.5">
                <OrgVisibilityToggle
                  label={t('sharing.visibleToOrg', { orgName })}
                  description={t('sharing.orgStudiesList')}
                  checked={isOrgVisibleToggleOn}
                  onCheckedChange={handleOrgVisibilityToggle}
                  disabled={share.saving}
                />
              </div>
            )}

            {selectedDropdownMode !== 'private' && share.supportsGuestChat && (
              <GuestChatAccessSection
                label={t('sharing.allowGuestChat')}
                description={t('sharing.guestChatOwnerPays')}
                enabled={share.guestChatEnabled}
                cap={share.guestChatCap}
                onToggle={share.handleGuestChatToggle}
                onCapChange={share.handleGuestChatCapChange}
                disabled={share.saving}
              />
            )}
          </div>

          <hr className="mx-6 border-t border-border" />

          <PeopleWithAccessSection
            members={share.orgMembers}
            excludeUserIds={share.excludedUserIds}
            guests={share.guests}
            orgMemberEmails={orgMemberEmails}
            onAddGuest={share.handleAddGuest}
            onRemoveGuest={share.handleRemoveGuest}
          />
        </div>
      )}

      {activeTab === 'collaborate' && (
        <div className="flex flex-col gap-4 px-6 py-5">
          <span className="text-sm text-muted-foreground">
            {t('sharing.addPeopleViewStudy')}
          </span>

          <MemberSearchInput
            members={share.orgMembers}
            excludeUserIds={share.excludedUserIds}
            onSelect={share.handleAddCollaborator}
          />

          <CollaboratorList
            collaborators={share.collaborators}
            onRemove={share.handleRemoveCollaborator}
          />
        </div>
      )}
    </ShareModalShell>
  )
}
